import type { ProductKind } from 'src/services/mappings/portfolio/portfolio.const'
import {
  PRODUCT_CATEGORY,
  PRODUCT_KIND,
} from 'src/services/mappings/portfolio/portfolio.const'

export const PRODUCT_GROUP = {
  scpi: 'scpi',
  immob: 'immob',
  av: 'av',
  cc: 'cc',
  epargne: 'epargne',
  pea: 'pea',
  emprunt: 'emprunt',
  mobiliere: 'mobiliere',
  prevoyance: 'prevoyance',
  other: 'other',
} as const

export type ProductGroup = ValueOf<typeof PRODUCT_GROUP>

export const PRODUCT_GROUP_ORDER = {
  scpi: 6,
  immob: 5,
  av: 3,
  cc: 1,
  epargne: 2,
  pea: 4,
  emprunt: 9,
  mobiliere: 7,
  prevoyance: 8,
  other: 10,
} as const

export const PRODUCT_KIND_FOR_ESTIMATION: ProductKind[] = [
  PRODUCT_KIND.residencePrincipale,
  PRODUCT_KIND.residenceSecondaire,
  PRODUCT_KIND.autresBiensDUsage,
  PRODUCT_KIND.immeubleLocatifLoueNu,
  PRODUCT_KIND.immeubleLoueMeubleLmp,
  PRODUCT_KIND.immeubleLoueMeubleLmnp,
  PRODUCT_KIND.autresImmeublesDeRapport,
  PRODUCT_KIND.lmnpBouvard,
]

export const PRODUCT_KIND_FOR_ESTIMATION_PRICE_HUBBLE: ProductKind[] = [
  PRODUCT_KIND.residencePrincipale,
  PRODUCT_KIND.residenceSecondaire,
  PRODUCT_KIND.immeubleLocatifLoueNu,
  PRODUCT_KIND.immeubleLoueMeubleLmp,
  PRODUCT_KIND.immeubleLoueMeubleLmnp,
  PRODUCT_KIND.lmnpBouvard,
]

export const PRODUCT_CATEGORIES_FOR_ESTIMATION: PRODUCT_CATEGORY[] = [
  PRODUCT_CATEGORY.immobilierDeRapport,
  PRODUCT_CATEGORY.biensUsage,
]

// fields that need to be required for this section, driven by front exceptionally, see TF-2445
export const PRICE_HUBBLE_REQUIRED_FIELDS = [
  'address',
  'countryCode',
  'surface',
  'propertyType',
  'dealType',
  'landArea',
  'buildingYear',
]

export const PRICE_HUBBLE_FIELDS = [
  'address',
  'balconyArea',
  'buildingYear',
  'condition',
  'conditionBathroom',
  'conditionFlooring',
  'conditionKitchen',
  'conditionMasonry',
  'conditionWindows',
  'countryCode',
  'dealType',
  'floors',
  'gardenSurface',
  'hasLift',
  'hasPool',
  'hasSauna',
  'isFurnished',
  'isNew',
  'landArea',
  'latitude',
  'longitude',
  'nbFloors',
  'nbOfIndoorParkingSpaces',
  'nbOfOutdoorParkingSpaces',
  'nbRooms',
  'notaryFees',
  'numberOfBathrooms',
  'propertyType',
  'quality',
  'qualityBathroom',
  'qualityFlooring',
  'qualityKitchen',
  'qualityMasonry',
  'qualityWindows',
  'surface',
]

export const PRODUCT_KIND_PRODUCT_GROUP: Record<ProductKind, string> = {
  assurance_vie: PRODUCT_GROUP.av,
  contrat_vie_generation: PRODUCT_GROUP.av,
  pep_assurance_vie: PRODUCT_GROUP.av,
  article_39: PRODUCT_GROUP.av,

  immeuble_loue_meuble_lmp: PRODUCT_GROUP.other,
  droits_sociaux_pacte_dutreil: PRODUCT_GROUP.other,
  droits_sociaux_de_salaries_mandataires: PRODUCT_GROUP.other,
  autres_biens_professionnels: PRODUCT_GROUP.other,
  entreprise_individuelle: PRODUCT_GROUP.other,
  fonds_de_commerce_clienteles: PRODUCT_GROUP.other,
  immeubles_professionnels_hors_lmp: PRODUCT_GROUP.other,
  parts_de_gfa_gaf_gfv_et_gfr_groupements_fonciers: PRODUCT_GROUP.other,
  biens_ruraux_loues_a_long_terme: PRODUCT_GROUP.other,
  droits_sociaux: PRODUCT_GROUP.other,
  sans_credit_dimpot: PRODUCT_GROUP.other,
  credit_dimpot_acquisition_rp_dans_le_neuf: PRODUCT_GROUP.other,
  credit_dimpot_acquisition_rp_dans_le_vieux: PRODUCT_GROUP.other,
  credit_dimpot_batiment_basse_consommation: PRODUCT_GROUP.other,
  actif_inconnu: PRODUCT_GROUP.other,

  compte_courant: PRODUCT_GROUP.cc,

  credit_a_la_consommation: PRODUCT_GROUP.emprunt,
  credit_facilites_de_tresorerie: PRODUCT_GROUP.emprunt,
  credit_dettes_fiscales: PRODUCT_GROUP.emprunt,
  credit_dettes_familiales: PRODUCT_GROUP.emprunt,
  credit_autres_dettes_diverses: PRODUCT_GROUP.emprunt,
  passif_inconnu: PRODUCT_GROUP.emprunt,
  credit_biens_dusage: PRODUCT_GROUP.emprunt,
  credit_biens_de_rapport: PRODUCT_GROUP.emprunt,
  credit_scpi_et_sci: PRODUCT_GROUP.emprunt,
  credit_autres_biens_immobiliers: PRODUCT_GROUP.emprunt,
  credit_droits_sociaux: PRODUCT_GROUP.emprunt,
  credit_fonds_de_commerce: PRODUCT_GROUP.emprunt,
  credit_immobilier_professionnel: PRODUCT_GROUP.emprunt,
  credit_autres_biens_professionnels: PRODUCT_GROUP.emprunt,

  compte_sur_livret_csl: PRODUCT_GROUP.epargne,
  livret_de_developpement_durable_et_solidaire_ldds: PRODUCT_GROUP.epargne,
  livret_a: PRODUCT_GROUP.epargne,
  livret_bleus: PRODUCT_GROUP.epargne,
  livret_depargne_populaire_lep: PRODUCT_GROUP.epargne,
  livret_jeune: PRODUCT_GROUP.epargne,
  compte_epargne_logement_cel: PRODUCT_GROUP.epargne,
  compte_a_terme: PRODUCT_GROUP.epargne,
  compte_courant_dassocies: PRODUCT_GROUP.epargne,
  bons_de_caisse: PRODUCT_GROUP.epargne,
  autres_disponibilites: PRODUCT_GROUP.epargne,
  plan_depargne_logement_pel: PRODUCT_GROUP.epargne,
  pep_bancaire: PRODUCT_GROUP.epargne,
  bons_et_contrats_de_capitalisation: PRODUCT_GROUP.epargne,
  autres_depots: PRODUCT_GROUP.epargne,
  peepei: PRODUCT_GROUP.epargne,
  per_individuel: PRODUCT_GROUP.epargne,
  per_entreprise_obligatoire: PRODUCT_GROUP.epargne,
  percopercoi: PRODUCT_GROUP.epargne,
  perp: PRODUCT_GROUP.epargne,
  madelin: PRODUCT_GROUP.epargne,
  madelin_agricole: PRODUCT_GROUP.epargne,
  article_83: PRODUCT_GROUP.epargne,
  article_82: PRODUCT_GROUP.epargne,
  prefon_retraite: PRODUCT_GROUP.epargne,
  retraite_mutualiste_du_combattant: PRODUCT_GROUP.epargne,

  residence_principale: PRODUCT_GROUP.immob,
  residence_secondaire: PRODUCT_GROUP.immob,
  terrain: PRODUCT_GROUP.immob,
  autres_biens_dusage: PRODUCT_GROUP.immob,
  meuble_meublant: PRODUCT_GROUP.immob,
  terrains_agricoles: PRODUCT_GROUP.immob,
  parts_de_groupements_forestiers: PRODUCT_GROUP.immob,
  bois_et_forets: PRODUCT_GROUP.immob,
  parts_de_societes_depargne_forestiere: PRODUCT_GROUP.immob,
  objets_dart_et_antiquites: PRODUCT_GROUP.immob,
  autres_placements_divers: PRODUCT_GROUP.immob,
  immeuble_locatif_loue_nu: PRODUCT_GROUP.immob,
  immeuble_loue_meuble_lmnp: PRODUCT_GROUP.immob,
  lmnp_bouvard: PRODUCT_GROUP.immob,
  parts_de_sci: PRODUCT_GROUP.immob,
  autres_immeubles_de_rapport: PRODUCT_GROUP.immob,

  pea: PRODUCT_GROUP.pea,
  pea_pme: PRODUCT_GROUP.pea,
  compte_titres: PRODUCT_GROUP.pea,

  temporaire_deces: PRODUCT_GROUP.prevoyance,
  vie_entiere: PRODUCT_GROUP.prevoyance,
  capital_differe: PRODUCT_GROUP.prevoyance,
  contrat_prevoyance_individuelle: PRODUCT_GROUP.prevoyance,
  convention_obseques: PRODUCT_GROUP.prevoyance,
  homme_cle: PRODUCT_GROUP.prevoyance,

  parts_de_scpi: PRODUCT_GROUP.scpi,

  sicav_fcp_actions: PRODUCT_GROUP.mobiliere,
  sicav_fcp_obligations: PRODUCT_GROUP.mobiliere,
  sicav_fcp_monetaire: PRODUCT_GROUP.mobiliere,
  actions_francaises_et_europeennes: PRODUCT_GROUP.mobiliere,
  actions_etrangeres: PRODUCT_GROUP.mobiliere,
  obligations: PRODUCT_GROUP.mobiliere,
  stock_options: PRODUCT_GROUP.mobiliere,
  sofica: PRODUCT_GROUP.mobiliere,
  fcpi: PRODUCT_GROUP.mobiliere,
  fcpr: PRODUCT_GROUP.mobiliere,
  fip: PRODUCT_GROUP.mobiliere,
  fip_corse: PRODUCT_GROUP.mobiliere,
  parts_de_holding: PRODUCT_GROUP.mobiliere,
  capital_de_pmetpe: PRODUCT_GROUP.mobiliere,
  autres_droits_sociaux: PRODUCT_GROUP.mobiliere,
  autres_valeurs_mobilieres: PRODUCT_GROUP.mobiliere,
}

export const PRODUCT_GROUPS_INVESTMENT: ProductGroup[] = [
  PRODUCT_GROUP.cc,
  PRODUCT_GROUP.epargne,
  PRODUCT_GROUP.av,
  PRODUCT_GROUP.pea,
  PRODUCT_GROUP.scpi,
  PRODUCT_GROUP.mobiliere,
]

export const PRODUCT_GROUPS_LOAN: ProductGroup[] = [PRODUCT_GROUP.emprunt]
export const PRODUCT_GROUPS_INSURANCE: ProductGroup[] = [
  PRODUCT_GROUP.prevoyance,
]
export const PRODUCT_GROUPS_REAL_ESTATE: ProductGroup[] = [PRODUCT_GROUP.immob]

export const PRODUCT_GROUP_KEYS = {
  productKind: 'productKind',
  productCategory: 'productCategory',
  productCategoryNew: 'productCategoryNew',
  bankName: 'bankName',
  productGroup: 'productGroup',
  productGroupMacro: 'productGroupMacro',
  productGroupFromProductKind: 'productGroupFromProductKind',
  reportCategory: 'reportCategory',
} as const

export const PRODUCT_GROUP_BY_ENTITY = [
  PRODUCT_GROUP_KEYS.productGroup,
  PRODUCT_GROUP_KEYS.bankName,
  PRODUCT_GROUP_KEYS.productCategory,
  PRODUCT_GROUP_KEYS.reportCategory,
]

export type ProductGroupByEntity = (typeof PRODUCT_GROUP_BY_ENTITY)[0]

export const REPORT_PRODUCT_CATEGORIES = {
  realestate: 'realestate',
  professional: 'professional',
  financial: 'financial',
  loan: 'loan',
} as const

export type ReportProductCategory = ValueOf<typeof REPORT_PRODUCT_CATEGORIES>

export const REPORT_PRODUCT_CATEGORY_ORDER = {
  [REPORT_PRODUCT_CATEGORIES.realestate]: 1,
  [REPORT_PRODUCT_CATEGORIES.professional]: 2,
  [REPORT_PRODUCT_CATEGORIES.financial]: 3,
  [REPORT_PRODUCT_CATEGORIES.loan]: 4,
} as const

export const SECTION_KEYS = {
  riskLevelGauge: 'riskLevelGauge',
  beneficiaryClause: 'beneficiaryClause',
  esg: 'ESGSensibility',
  other: 'other',
}

export const COUPLE_ID = 'couple'
